'use client';

import { Z_INDEX } from '@/constants';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface Tab {
  label: string;
  id: string;
}

interface CommonTopMenuTabProps {
  tabs: Tab[];
}

export default function CommonTopMenuTab({ tabs }: CommonTopMenuTabProps) {
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id || '');
  const [isMenuFixed, setIsMenuFixed] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(136); // 기본값
  const [hasSectionTabs, setHasSectionTabs] = useState<boolean>(false); // SectionTabs 존재 여부
  const menuRef = useRef<HTMLDivElement>(null);
  const menuOriginalTopRef = useRef<number | null>(null); // 메뉴의 원래 위치

  const handleTabClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const menuHeight = 60; // 메뉴 높이
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - menuHeight - headerHeight;

      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth',
      });
    }
  };

  // 헤더 높이 측정 및 SectionTabs 존재 여부 확인
  useEffect(() => {
    const measureHeader = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.getBoundingClientRect().height);

        // SectionTabs 존재 여부 확인 (실제 렌더링 크기로 확인)
        const sectionTabs = header.querySelector('[data-section-tabs="true"]');
        const hasTabs =
          sectionTabs !== null &&
          sectionTabs.getBoundingClientRect().height > 0;
        setHasSectionTabs(hasTabs);
      }
    };

    measureHeader();
    window.addEventListener('resize', measureHeader);

    return () => {
      window.removeEventListener('resize', measureHeader);
    };
  }, []);

  // 초기 메뉴 위치 저장
  useEffect(() => {
    const initMenuPosition = () => {
      if (menuRef.current && menuOriginalTopRef.current === null) {
        menuOriginalTopRef.current = menuRef.current.offsetTop;
      }
    };

    // DOM이 렌더링된 후 위치 저장
    setTimeout(initMenuPosition, 0);
    initMenuPosition();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!menuRef.current || menuOriginalTopRef.current === null) return;

      const scrollY = window.scrollY;
      const menuOriginalTop = menuOriginalTopRef.current;

      // 스크롤이 메뉴의 원래 위치(헤더 높이 제외)보다 위로 가면 fixed 해제
      const threshold = menuOriginalTop - headerHeight;
      if (scrollY <= threshold) {
        setIsMenuFixed(false);
      } else {
        // 스크롤이 threshold보다 아래로 가면 fixed로 전환
        setIsMenuFixed(true);
      }

      // 활성 탭 결정
      const menuHeight = 60;
      const activeTabThreshold = menuHeight + headerHeight;
      // 섹션이 threshold보다 조금 더 전에 활성화 (-5px 여유)
      const activationOffset = -5;

      for (let i = tabs.length - 1; i >= 0; i--) {
        const element = document.getElementById(tabs[i].id);
        if (element) {
          const elementRect = element.getBoundingClientRect();
          // 요소의 상단이 activeTabThreshold - activationOffset보다 위에 있으면 활성화
          if (elementRect.top <= activeTabThreshold - activationOffset) {
            setActiveTab(tabs[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 실행

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerHeight, tabs]);

  // tabs가 변경되면 첫 번째 탭을 기본값으로 설정
  useEffect(() => {
    if (tabs.length > 0 && tabs[0]?.id) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs]);

  return (
    <div
      ref={menuRef}
      className={`desktop:bg-level-2 flex w-full items-center justify-center ${
        isMenuFixed ? 'fixed right-0 left-0' : 'relative'
      } `}
      style={{
        top: isMenuFixed
          ? hasSectionTabs
            ? headerHeight - 24
            : headerHeight
          : undefined,
        zIndex: Z_INDEX.SubHeader,
      }}
    >
      <div className="desktop:web-w mobile-w desktop:w-full desktop:px-0 desktop:gap-[52px] relative flex h-[60px] w-full flex-row items-center justify-center gap-2">
        <div className="desktop:hidden absolute top-0 mx-auto h-full w-full">
          <div
            className={cn(
              'bg-level-2 h-full',
              isMenuFixed ? 'mobile-w w-full' : '-ml-10 w-[calc(100%+80px)]',
            )}
          ></div>
        </div>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`bg-level-2 typo-16 desktop:typo-18 desktop:w-[160px] desktop:flex-none z-1 h-full flex-1 cursor-pointer font-semibold transition hover:text-[#EEEEEE] ${
                isActive
                  ? 'border-b border-white text-[#EEEEEE]'
                  : 'text-[#999999]'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
